/**
 * Engine prefill: match car.info's raw `engine` string against a make's catalog chips.
 *
 * Pure and React-free so the matching rules AND the async-catalog ordering can both be
 * unit tested in a node environment, without jsdom or component rendering.
 *
 * Validated against the recorded payload bank and the live 357-chip catalog: two exact
 * chip hits (Skoda "1.6 TDI", Opel "1.2 Turbo"), zero prefix hits, nine raw fallbacks.
 */

/** Chips that are never valid prefix candidates. */
const NON_CANDIDATE_CHIPS = new Set(["electric", "övrigt"]);

/** lowercase + trim + collapse internal whitespace. */
export const normalizeEngine = (value: string): string =>
  value.toLowerCase().trim().replace(/\s+/g, " ");

/**
 * Resolve a raw engine string to a canonical chip name, or return it unchanged.
 *
 * Step 1 - normalized equality against any chip.
 * Step 2 - a chip is a PREFIX of the engine string, with a mandatory following space
 *          ("1.6 TDI" matches "1.6 TDI DSG" but not "1.6 TDIX"). Never the reverse
 *          direction, so a bare "1.6" can never select the "1.6 TDI" chip. Accepted only
 *          when exactly one chip qualifies; 2+ is ambiguous and falls through.
 * Step 3 - no match: return the raw string. DetailsSection renders a non-chip value as a
 *          highlighted "Övrigt" option pre-filled with this text.
 *
 * @param rawEngine raw car.info engine descriptor
 * @param chips     the selected make's catalog engine names
 */
export const matchEngineToChip = (rawEngine: string, chips: string[]): string => {
  const engine = normalizeEngine(rawEngine);
  if (engine === "") return rawEngine;

  // Step 1 - exact (normalized) equality.
  const exact = chips.find((chip) => normalizeEngine(chip) === engine);
  if (exact) return exact;

  // Step 2 - unique chip prefix.
  const candidates = chips.filter((chip) => {
    const c = normalizeEngine(chip);
    if (c === "" || NON_CANDIDATE_CHIPS.has(c)) return false;
    return engine === c || engine.startsWith(`${c} `);
  });
  if (candidates.length === 1) return candidates[0];

  // Step 3 - ambiguous or unmatched: keep the raw string.
  return rawEngine;
};

export interface ResolveEngineArgs {
  /** The engine value currently in the form (raw string written at lookup time). */
  currentEngine: string | undefined;
  /** The selected make's catalog chips. Empty while the catalog is still loading. */
  chips: string[];
  /** True only when the form was populated by a plate lookup. */
  isAutoLoaded: boolean;
  /** One-shot latch: whether this auto-load has already been resolved. */
  alreadyResolved: boolean;
}

/**
 * Decide whether the catalog-load effect should upgrade the engine field, and to what.
 *
 * The ordering problem this solves: the makes catalog loads asynchronously, so at lookup
 * time there are no chips to match against. The selector therefore writes the RAW engine
 * string immediately (which is already the desired fallback), and this runs once the
 * catalog arrives to upgrade raw -> canonical chip where possible. If the catalog never
 * loads, the raw string simply stays.
 *
 * It refuses to act when:
 *  - the form was not auto-loaded (a manually entered engine is the dealer's own)
 *  - the chips have not arrived yet
 *  - it has already run for this auto-load (one-shot)
 *  - the current value is ALREADY one of the chips - i.e. a dealer's explicit pick, or an
 *    upgrade we performed earlier. This is what guarantees a dealer's choice is never
 *    clobbered by a late-arriving catalog.
 */
export const resolveEngineOnCatalogLoad = (
  args: ResolveEngineArgs
): { shouldSet: boolean; value?: string } => {
  const { currentEngine, chips, isAutoLoaded, alreadyResolved } = args;

  if (!isAutoLoaded) return { shouldSet: false };
  if (alreadyResolved) return { shouldSet: false };
  if (!currentEngine || currentEngine.trim() === "") return { shouldSet: false };
  if (chips.length === 0) return { shouldSet: false };

  // EXACT comparison, deliberately not normalized: DetailsSection highlights the selected
  // chip with `engine === engineName`, so a value that merely normalizes to a chip
  // ("1.6 tdi" vs the "1.6 TDI" chip) would render as pre-typed Övrigt text instead of a
  // selected chip. Those values must still be upgraded to the canonical casing.
  // A dealer's pick is byte-identical to a chip, so it is still never touched.
  const isAlreadyChip = chips.some((chip) => chip === currentEngine);
  if (isAlreadyChip) return { shouldSet: false };

  const resolved = matchEngineToChip(currentEngine, chips);
  if (resolved === currentEngine) return { shouldSet: false };

  return { shouldSet: true, value: resolved };
};

export interface ExposeEngineOtherArgs {
  /** Current engine value in the form. */
  engine: string | undefined;
  /** The selected make's catalog chips (excluding "Övrigt"). */
  chips: string[];
  /** False while the makes catalog is still loading. */
  catalogLoaded: boolean;
}

/**
 * Should the "Övrigt" free-text box be shown expanded and pre-filled?
 *
 * A prefilled engine that matches no catalog chip used to be invisible until the dealer
 * clicked "Övrigt" - so the value existed but could not be verified at a glance. It must
 * be visible immediately instead.
 *
 * Waiting for `catalogLoaded` matters: during loading `chips` is momentarily empty, and
 * without the guard a value that IS a chip would flash the free-text box open before the
 * catalog arrived and the chip resolved. Once loaded, an empty chip list is a real answer
 * (a make absent from the catalog), and the box correctly opens.
 *
 * Comparison is EXACT, matching how DetailsSection highlights chips (`engine === name`).
 */
export const shouldExposeEngineOtherInput = (args: ExposeEngineOtherArgs): boolean => {
  const { engine, chips, catalogLoaded } = args;
  if (!engine || engine.trim() === "") return false;
  if (!catalogLoaded) return false;
  return !chips.some((chip) => chip === engine);
};
